import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X } from 'lucide-react';
import { api } from '../lib/api';
import { trackSearch } from '../lib/tracking';
import { CATEGORIES, TREATMENT_SLUGS } from '../data/categories';
import { PRICE_GUIDES } from '../data/prices';
import { ARTICLES } from '../data/articles';

const SEARCH_CORPUS = () => {
  const items = [];
  // Treatments
  Object.entries(TREATMENT_SLUGS).forEach(([slug, name]) => {
    const cat = CATEGORIES.find((c) => c.treatments.includes(name));
    items.push({ type: 'Behandling', title: name, href: `/behandlinger/${cat?.slug || 'estetikk-og-kosmetikk'}`, tag: cat?.name });
  });
  // Price guides
  Object.entries(PRICE_GUIDES).forEach(([slug, g]) => {
    items.push({ type: 'Prisguide', title: `Priser: ${g.shortLabel}`, href: `/priser/${slug}`, tag: g.category });
  });
  // Articles
  ARTICLES.forEach((a) => {
    items.push({ type: 'Artikkel', title: a.title, href: `/artikler/${a.slug}`, tag: a.category });
  });
  return items;
};

const corpus = SEARCH_CORPUS();

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/clinics/public').then((r) => setClinics(r.data.clinics || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const ql = q.toLowerCase();
    const items = [
      ...corpus.filter((r) => r.title.toLowerCase().includes(ql)),
      ...clinics.filter((c) => c.name.toLowerCase().includes(ql)).map((c) => ({
        type: 'Klinikk', title: c.name, href: `/klinikker/${c.id}`, tag: c.city,
      })),
    ].slice(0, 8);
    setResults(items);
  }, [q, clinics]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    trackSearch(q, results.length);
    navigate(`/sok?q=${encodeURIComponent(q)}`);
    setOpen(false);
    setShowDropdown(false);
  };

  useEffect(() => {
    const onClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <nav data-testid="main-nav" className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'backdrop-blur-md bg-[#0A1F44]/95 border-b border-white/10' : 'bg-[#0A1F44]'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
          <Link to="/" data-testid="nav-logo" className="flex-shrink-0">
            <span className="font-display text-xl lg:text-2xl font-bold text-white tracking-tight">
              Specialists<span className="text-[#3B82F6]">.no</span>
            </span>
          </Link>

          {/* Desktop search */}
          <div ref={dropdownRef} className="hidden md:flex flex-1 max-w-xl relative">
            <form onSubmit={handleSubmit} className="w-full">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={18} strokeWidth={1.8} />
                <input
                  data-testid="nav-search-input"
                  type="text"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Søk behandling, klinikk eller spesialist…"
                  className="w-full bg-white/10 border border-white/15 text-white placeholder-white/50 rounded-full px-12 py-2.5 focus:bg-white/15 focus:border-white/30 focus:outline-none transition-all text-sm"
                />
              </div>
            </form>
            {showDropdown && results.length > 0 && (
              <div data-testid="nav-search-dropdown" className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-2xl overflow-hidden border border-[#0A1F44]/10 z-50">
                {results.map((r, i) => (
                  <Link
                    key={i}
                    to={r.href}
                    onClick={() => { setShowDropdown(false); setQ(''); }}
                    className="flex items-center justify-between px-4 py-3 hover:bg-[#FAF8F5] transition-colors border-b border-[#0A1F44]/5 last:border-0"
                    data-testid={`search-result-${i}`}
                  >
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[#3B82F6] font-semibold">{r.type}</div>
                      <div className="text-[#0A1F44] font-medium">{r.title}</div>
                    </div>
                    <span className="text-xs text-[#64748B]">{r.tag}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="hidden lg:flex items-center gap-7 text-[15px] text-white/85">
            <Link to="/behandlinger/estetikk-og-kosmetikk" className="hover:text-white transition-colors" data-testid="nav-behandlinger">Behandlinger</Link>
            <Link to="/priser" className="hover:text-white transition-colors" data-testid="nav-priser">Priser</Link>
            <Link to="/artikler" className="hover:text-white transition-colors" data-testid="nav-artikler">Artikler</Link>
            <Link to="/#lead-form" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-5 py-2.5 rounded-full font-semibold transition-colors text-sm shadow-md" data-testid="nav-cta">
              Finn spesialist →
            </Link>
          </div>

          <button
            data-testid="nav-hamburger"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Åpne meny"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile search */}
        <div className="md:hidden pb-3">
          <form onSubmit={handleSubmit}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={18} />
              <input
                data-testid="nav-search-input-mobile"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Søk behandling eller klinikk…"
                className="w-full bg-white/10 border border-white/15 text-white placeholder-white/50 rounded-full px-12 py-2.5 text-sm focus:outline-none"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 top-16 bg-[#0A1F44] z-30 flex flex-col p-8 gap-6 text-white text-lg overflow-y-auto" data-testid="mobile-menu">
          <Link to="/behandlinger/estetikk-og-kosmetikk" onClick={() => setOpen(false)}>Behandlinger</Link>
          <Link to="/priser" onClick={() => setOpen(false)}>Priser</Link>
          <Link to="/artikler" onClick={() => setOpen(false)}>Artikler</Link>
          <Link to="/klinikker" onClick={() => setOpen(false)}>Klinikker</Link>
          <Link to="/om-oss" onClick={() => setOpen(false)}>Om oss</Link>
          <Link to="/slik-fungerer-det" onClick={() => setOpen(false)}>Slik fungerer det</Link>
          <Link to="/for-klinikker" onClick={() => setOpen(false)}>For klinikker</Link>
          <Link to="/#lead-form" onClick={() => setOpen(false)} className="bg-[#3B82F6] text-white py-3 rounded-full text-center font-semibold mt-4">
            Finn spesialist →
          </Link>
        </div>
      )}
    </nav>
  );
}
